/**
 * iter_194b — Public Presence Status Badge.
 *
 * Renders the artist's current presence state + custom note as a small
 * pill on the profile header (and any other public surface that shows
 * users). Hidden when status is Online or Offline (or missing) — nothing
 * to broadcast in those cases.
 *
 * Data shape (from public endpoints — /api/artist/{id}, /api/presence/active):
 *   • presence_status               "live" | "away" | "brb" | "online" | "offline" | null
 *   • presence_status_note          optional string
 *   • presence_status_updated_at    ISO timestamp (kept for freshness gating)
 *
 * Freshness: rows older than 30 min are treated as stale and hidden.
 */
const META = {
  live: {
    label: "Live",
    dot: "bg-rose-400",
    chip: "bg-rose-500/15 border-rose-400/40 text-rose-100",
    icon: "🔴",
  },
  away: {
    label: "Away",
    dot: "bg-amber-400",
    chip: "bg-amber-500/15 border-amber-400/40 text-amber-100",
    icon: "🟡",
  },
  brb: {
    label: "BRB",
    dot: "bg-violet-400",
    chip: "bg-violet-500/15 border-violet-400/40 text-violet-100",
    icon: "🟣",
  },
};

const SIZE = {
  sm: "text-[11px] px-2 py-0.5 gap-1",
  md: "text-xs px-2.5 py-1 gap-1.5",
};

const FRESH_MS = 30 * 60 * 1000;

const PresenceStatusBadge = ({
  status,
  note,
  updatedAt,
  size = "sm",
  testid = "presence-status-badge",
  className = "",
}) => {
  const m = status ? META[status] : null;
  if (!m) return null; // hides for online/offline/null
  // Freshness gate: rows older than 30 min are stale and should not
  // linger on the profile as if the person were still Away.
  if (updatedAt) {
    const t = new Date(updatedAt).getTime();
    if (!Number.isNaN(t) && Date.now() - t > FRESH_MS) return null;
  }
  const cleanNote = typeof note === "string" ? note.trim().slice(0, 80) : "";
  return (
    <span
      className={`inline-flex items-center rounded-full border font-semibold whitespace-nowrap ${m.chip} ${SIZE[size] || SIZE.sm} ${className}`}
      data-testid={testid}
      title={cleanNote ? `${m.label} · ${cleanNote}` : m.label}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${m.dot}`} aria-hidden />
      <span data-testid={`${testid}-label`}>{m.label}</span>
      {cleanNote && (
        <>
          <span className="opacity-60">·</span>
          <span
            className="truncate max-w-[220px]"
            data-testid={`${testid}-note`}
          >
            {cleanNote}
          </span>
        </>
      )}
    </span>
  );
};

export default PresenceStatusBadge;
